import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CommonButton from "../CommonButton";

type SizeSelectorProps = {
    sizes: string[];
    selectedSize: string | undefined;
    onSelectSize: (size: string) => void;
    gap?: number;
    itemPadding?: string;
    textSize?: string;
    title?: string;
    // Add this prop to render the discount tag directly below the button
    renderItemBottom?: (size: string) => React.ReactNode; 
};

const SizeSelector: React.FC<SizeSelectorProps> = ({
    sizes,
    selectedSize,
    onSelectSize,
    itemPadding = "",
    textSize = "",
    title = "Select Size",
    renderItemBottom
}) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [isScrollable, setIsScrollable] = React.useState(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScroll = () => {
            setIsScrollable(el.scrollWidth > el.clientWidth);
        };

        checkScroll();

        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, [sizes]);

    return (
        <div onClick={(e) => e.stopPropagation()} className="w-full">
            <p className="text-sm text-mainBrandColor mb-2 font-semibold">
                {title}
            </p>

            <div className="flex items-center gap-2">
                {isScrollable && (
                    <CommonButton
                        icon={<ChevronLeft size={14} />}
                        onClick={() => {
                            scrollRef.current?.scrollBy({
                                left: -120,
                                behavior: "smooth",
                            });
                        }}
                    />
                )}

                <div
                    ref={scrollRef}
                    className="flex gap-4 overflow-x-auto no-scrollbar scroll-smooth flex-1 pb-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    {sizes.map((size, i) => {
                        const isSelected = selectedSize === size;

                        return (
                            // Flex column arranges the box and the percentage tag vertically
                            <div key={i} className="flex flex-col items-center gap-1 shrink-0">
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectSize(size);
                                    }}
                                    className={`
                                        ${itemPadding || "px-4 py-2"}
                                        ${textSize || "text-xs"}
                                        rounded-xl border whitespace-nowrap cursor-pointer transition-all duration-200 text-center min-w-[60px]
                                        ${isSelected
                                            ? "bg-mainBrandColor text-white border-mainBrandColor font-bold shadow-sm scale-105"
                                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-slate-50"}
                                    `}
                                >
                                    {size}
                                </div>
                                
                                {/* Renders the 10% or 12% directly below the button item */}
                                {renderItemBottom && renderItemBottom(size)}
                            </div>
                        );
                    })}
                </div>

                {isScrollable && (
                    <CommonButton
                        icon={<ChevronRight size={14} />}
                        onClick={() => {
                            scrollRef.current?.scrollBy({
                                left: 120,
                                behavior: "smooth",
                            });
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SizeSelector;